import type { Condition, Extension } from '@medplum/fhirtypes';

/**
 * Frontend mirror of the clinical-findings review markers for Condition —
 * see `libs/backend/mini-apps/src/lib/agents/clinical-findings/systems.ts`
 * and `finding-types/condition.ts` (the backend source of truth for these
 * semantics). The systems are environment-independent constants rooted at
 * the centralized Eluve FHIR namespace (ENG-4575), so mirroring the strings
 * is safe; the backend lib itself is not importable from frontend code.
 */
const ELUVE_FHIR_NAMESPACE = 'https://eluve.com';

/** meta.tag system carrying the human-review state of an AI finding. */
export const REVIEW_STATUS_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/review-status`;
export const REVIEW_STATUS_PENDING = 'pending';
/** Human approved the finding → verificationStatus confirmed (or refuted for denials). */
export const REVIEW_STATUS_ATTESTED = 'attested';
/** Human rejected the finding → verificationStatus entered-in-error. */
export const REVIEW_STATUS_REJECTED = 'rejected';

/** Extension carrying the agent's 0–1 confidence in an extracted finding. */
export const FINDING_CONFIDENCE_EXTENSION = `${ELUVE_FHIR_NAMESPACE}/finding-confidence`;

export const CONDITION_VER_STATUS_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/condition-ver-status';

/** The condition's verification code (provisional, confirmed, refuted, …). */
export function verificationCode(condition: Condition): string | undefined {
  return condition.verificationStatus?.coding?.[0]?.code;
}

/** Retracted (dismissed / superseded) — off the problem list everywhere. */
export function isRetracted(condition: Condition): boolean {
  return verificationCode(condition) === 'entered-in-error';
}

/**
 * An explicitly denied finding ("denies chest pain") the agent wrote as
 * `refuted`. While pending it still needs review (attesting the denial was
 * correctly captured); once reviewed it is not a problem-list entry.
 */
export function isRefuted(condition: Condition): boolean {
  return verificationCode(condition) === 'refuted';
}

/**
 * Still awaiting human review: the uniform `review-status: pending` tag, or —
 * for conditions written outside the agent pipeline — a bare
 * `provisional`/`unconfirmed` verificationStatus (ticket scope treats both as
 * unverified). Retracted resources are never pending, even when a stale
 * pending tag lingers after a supersede.
 */
export function isPendingReview(condition: Condition): boolean {
  if (isRetracted(condition)) return false;
  const verification = verificationCode(condition);
  if (verification === 'provisional' || verification === 'unconfirmed') {
    return true;
  }
  return Boolean(
    condition.meta?.tag?.some(
      (tag) =>
        tag.system === REVIEW_STATUS_SYSTEM &&
        tag.code === REVIEW_STATUS_PENDING,
    ),
  );
}

/** The supporting transcript passage stored on a finding (first note.text). */
export function readEvidence(condition: Condition): string | null {
  return condition.note?.[0]?.text ?? null;
}

/** The 0–1 confidence stamped on a finding's extension, or null. */
export function readConfidence(condition: Condition): number | null {
  const extension = (condition as { extension?: Extension[] }).extension?.find(
    (e) => e.url === FINDING_CONFIDENCE_EXTENSION,
  );
  return extension?.valueDecimal ?? null;
}

/**
 * Replace the `review-status` meta.tag (pending → attested / rejected),
 * preserving every other tag — same transition the backend reviewer applies.
 */
export function setReviewStatusTag(
  condition: Condition,
  code: string,
): Condition {
  const others = (condition.meta?.tag ?? []).filter(
    (tag) => tag.system !== REVIEW_STATUS_SYSTEM,
  );
  return {
    ...condition,
    meta: {
      ...condition.meta,
      tag: [...others, { system: REVIEW_STATUS_SYSTEM, code }],
    },
  };
}
